import { localToday } from '../../lib/flightFilters';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import type { SquawkSeverity } from '../../lib/types';
import { DEMO_AIRCRAFT } from '../../lib/demoData';
import { SQUAWK_SEV_LABEL, SQUAWK_SEV_DESC } from '../../lib/squawks';

import { TRACKED_REG } from '../../lib/flightTrack';
import { FilePicker } from '../../components/FilePicker';
const SEV_OPTIONS: SquawkSeverity[] = ['informational', 'should_fix_soon', 'grounding'];

export function ReportSquawk() {
  const { profile, isAdmin, demoMode } = useAuth();
  const nav = useNavigate();
  const [severity, setSeverity] = useState<SquawkSeverity>('should_fix_soon');
  const [description, setDescription] = useState('');
  const [dateDiscovered, setDateDiscovered] = useState(localToday());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!description.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      // Upload photos first (so the new squawk doc is created once, with the URLs).
      let photos: string[] = [];
      const selected = fileRef.current?.files;
      if (!demoMode && db && storage && selected?.length && profile?.uid) {
        for (let i = 0; i < selected.length; i++) {
          const f = selected[i];
          const sref = storageRef(storage, `squawks/${profile.uid}/${Date.now()}-${i}-${f.name}`);
          await uploadBytes(sref, f);
          photos.push(await getDownloadURL(sref));
        }
      }
      const baseName = [profile?.personal?.firstName, profile?.personal?.lastName].filter(Boolean).join(' ');
      // Tag owner-filed squawks so pilots reading the shared log know who found it.
      const reporterName = baseName && isAdmin ? `${baseName} (owner)` : baseName;
      const payload = {
        // TRACKED_REG, not DEMO_AIRCRAFT — that array is a module constant, so
        // `DEMO_AIRCRAFT[0]?.aircraftId ?? …` always resolved to the DEMO tail and
        // the fallback never fired. Live squawks were being written against N123AB
        // from 2026-09-11 (when demoData moved to N123AB) until 2026-09-13.
        aircraftId: TRACKED_REG,
        reportedBy: profile?.uid ?? 'unknown',
        ...(reporterName ? { reportedByName: reporterName } : {}),
        severity,
        description: description.trim(),
        photos,
        dateDiscovered,
        status: 'open' as const,
        isGrounding: severity === 'grounding',
      };
      if (!demoMode && db) {
        await addDoc(collection(db, 'squawks'), { ...payload, createdAt: serverTimestamp() as unknown as Timestamp });
      }
      setMsg(isAdmin ? 'Squawk logged.' : 'Squawk reported. The owner has been notified.');
      setTimeout(() => nav(isAdmin ? '/admin/squawks' : '/squawks'), 900);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Could not submit the squawk. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title={isAdmin ? 'Log a squawk' : 'Report a squawk'}
        subtitle={isAdmin ? 'Add a discrepancy to the shared squawk log.' : 'Tell the owner about a discrepancy.'}
      />
      <form onSubmit={submit} className="space-y-6">
        <div className="card p-5">
          <h2 className="font-display text-lg font-semibold text-navy-900 mb-3">Severity</h2>
          <div className="space-y-2">
            {SEV_OPTIONS.map((o) => (
              <label
                key={o}
                className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border ${
                  severity === o ? 'border-brand bg-sky-50' : 'border-slate-200 hover:bg-sky-50/50'
                }`}
              >
                <input
                  type="radio"
                  checked={severity === o}
                  onChange={() => setSeverity(o)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-navy-900">{SQUAWK_SEV_LABEL[o]}</div>
                  <div className="text-xs text-ink-muted">{SQUAWK_SEV_DESC[o]}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="card p-5 space-y-4">
          <div>
            <label className="label">Date discovered</label>
            <div className="relative">
              <CalendarDays size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-600" />
              <input
                type="date"
                className="input cursor-pointer bg-sky-50/60 pl-10 pr-9 font-medium text-navy-900"
                value={dateDiscovered}
                onChange={(e) => setDateDiscovered(e.target.value)}
              />
              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-navy-500" />
            </div>
          </div>
          <div>
            <label className="label">Description</label>
            <textarea
              className="input-textarea min-h-[120px]"
              required
              placeholder="What did you find? Be specific — location on the aircraft, conditions, anything you tried."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Photos (optional)</label>
            <FilePicker ref={fileRef} accept="image/*" multiple chooseLabel="Choose photos" />
            <p className="mt-1 text-xs text-ink-muted">A photo of the issue helps the owner diagnose it fast.</p>
          </div>
        </div>

        {msg && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 text-sm">
            {msg}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => nav(-1)} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Submitting…' : isAdmin ? 'Log squawk' : 'Submit squawk'}
          </button>
        </div>
      </form>
    </>
  );
}
